import DialogContainer from "../../components/common/DialogContainer";
import { setSocket, fetchAppointmentUnreadCount, fetchMessagesUnreadCount, setMessagesUnreadCount, incrementMessagesUnreadCount } from "../../redux/user-slices/socket.slice";
import { Outlet, useLocation } from "react-router-dom";
import { SOCKET_BASEURI } from "../../utils/helper";
import { useEffect, useRef, useState } from "react";
import Toaster from "../../components/ui/Toaster";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "../common/Sidebar";
import Header from "../common/Header";
import Footer from "../common/Footer";
import { io } from "socket.io-client";
import { Box } from "@mui/material";
import { handleGetMySubscription } from "../../redux/member-slices/payment.slice";
import { setSubscription } from "../../redux/user-slices/subscription.slice";
import { showToast } from "../../redux/guest-slices/other.slice";
import { useFirebaseNotifications } from "../../hooks/useFirebaseNotifications";

import { playReceiveMessageSound } from "../../utils/sound.utils";

function Layout() {
  useFirebaseNotifications();
  const socket = useRef(null);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [active, setActive] = useState(false);

  const sidebarActive = () => {
    setActive(true);
  };
  const sidebarDeActive = () => {
    setActive(false);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [pathname]);


  const { member } = useSelector((state) => state.authSlice);
  const currentChat = useSelector((state) => state.socketSlice.chat);

  useEffect(() => {
    const memberId = member?._id || member?.userId;
    if (memberId) {
      dispatch(fetchAppointmentUnreadCount());
      dispatch(fetchMessagesUnreadCount());
    }
  }, [dispatch, member]);

  useEffect(() => {
    socket.current = io.connect(SOCKET_BASEURI);
    if (!socket.current) return;
    dispatch(setSocket(socket.current));
    // eslint-disable-next-line
  }, [socket.current]);

  useEffect(() => {
    if (!socket.current) return;
    const memberId = member?._id || member?.userId;
    if (memberId) {
      socket.current.emit("join-user", memberId);
      socket.current.emit("get-chats", { userId: memberId, currentPage: 1, limit: 20 });
    }
  }, [socket.current, member]);

  useEffect(() => {
    if (!socket.current) return;

    const handleChatsList = ({ data }) => {
      if (Array.isArray(data?.records)) {
        const total = data.records.reduce((acc, r) => acc + (r.unreadCount || r.unread || 0), 0);
        dispatch(setMessagesUnreadCount(total));
      }
    };

    socket.current.on("chats-list", handleChatsList);
    return () => {
      socket.current?.off("chats-list", handleChatsList);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!socket.current) return;

    const handleNewMessageNotification = (data) => {
      playReceiveMessageSound();

      if (data.chat?.type === "appointment") {
        dispatch(fetchAppointmentUnreadCount());
      } else {
        if (data.unreadCount !== undefined) {
          dispatch(setMessagesUnreadCount(data.unreadCount));
        } else {
          dispatch(incrementMessagesUnreadCount(1));
        }
      }

      if (currentChat?._id === data.message.roomId) {
        return;
      }

      const userType = member?.userRole || "member";
      const messageText = data.message.type === "text" ? data.message.text : "sent a file";
      const senderName = data.chat?.user?.fullName || "Someone";
      
      const toastMsg = data.chat?.type === "appointment"
        ? `Appointment Message from ${senderName}: "${messageText}"`
        : `Message from ${senderName}: "${messageText}"`;

      dispatch(showToast({
        message: toastMsg,
        type: "info",
        duration: 5000,
        onClickPath: data.chat?.type === "appointment" 
          ? `/${userType}/profile/appointment-messages` 
          : `/${userType}/profile/messages`,
        onClickState: data.chat
      }));
    };

    socket.current.on("new-message-notification", handleNewMessageNotification);
    return () => {
      if (socket.current) {
        socket.current.off("new-message-notification", handleNewMessageNotification);
      }
    };
  }, [socket.current, currentChat, member, dispatch]);


  useEffect(() => {
    const loadSubscription = async () => {
      const response = await dispatch(
        handleGetMySubscription() 
      );

      if (response?.payload) {
        dispatch(
          setSubscription(response.payload)
        );

        if (response.payload?.hasSubscription || (response.payload?.plan?.name && response.payload?.plan?.name !== "basic")) {
          localStorage.removeItem("selectedSubscriptionPlan");
        }
      } else {
        dispatch(
          setSubscription({ hasSubscription: false, status: "none", plan: { name: "basic" } })
        );
      }
    };

    loadSubscription();
  }, []);

  return (
    <Box
      sx={{
        display: "grid",
        minHeight: "100vh",
        gridTemplateRows: "auto auto 1fr",
        overflowX: "hidden",
        maxWidth: "100%",
      }}
    >
      <Sidebar active={active} sidebarDeActive={sidebarDeActive} />
      <Header sidebarActive={sidebarActive} />
      <Box
        component="main"
        sx={{
          maxWidth: "100%",
          overflowX: "hidden",
        }}
      >
        <Outlet context={{ active }} />
      </Box>
      <Footer />
      <Toaster />
      <DialogContainer />
    </Box>
  );
}

export default Layout;
